"use client";
import { useEffect, useRef } from "react";
import { Room, RoomEvent } from "livekit-client";
import type { RemoteTrack, RemoteTrackPublication, RemoteParticipant } from "livekit-client";
import { Track } from "livekit-client";
import { playSound, setSoundEnabled } from "@client/utils/sounds";
import { useAppSelector } from "@client/store/store.hooks";
import { selectSoundEnabled } from "@client/store/slices/ui.slice";

interface UseMeetEventSoundsOptions {
  room: Room;
}

/**
 * Plays sound effects for key meeting events:
 * - Participant joins → ascending chime
 * - Screen share starts → descending chime
 *
 * Join-request sounds are triggered separately via useDataChannelManager.
 * Skips sounds during the initial 2s connection window to avoid a burst on join.
 * Respects the user's sound preference from Redux.
 */
export function useMeetEventSounds({ room }: UseMeetEventSoundsOptions): void {
  const initializedRef = useRef(false);
  const soundEnabled = useAppSelector(selectSoundEnabled);

  // Keep the sound utility in sync with Redux preference
  useEffect(() => {
    setSoundEnabled(soundEnabled);
  }, [soundEnabled]);

  useEffect(() => {
    // Delay activation to skip the initial participant list on connect
    const timer = setTimeout(() => {
      initializedRef.current = true;
    }, 2000);

    const handleParticipantConnected = () => {
      if (initializedRef.current) {
        playSound("join");
      }
    };

    const handleTrackSubscribed = (
      track: RemoteTrack,
      _publication: RemoteTrackPublication,
      _participant: RemoteParticipant
    ) => {
      if (initializedRef.current && track.source === Track.Source.ScreenShare) {
        playSound("screenshare");
      }
    };

    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);

    return () => {
      clearTimeout(timer);
      initializedRef.current = false;
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
    };
  }, [room]);
}
